import DateTimeOffsetHelper from '@shared/common/DateTimeOffsetHelper';
import { JsonValue, apiGet, apiPostForm, apiPostJson } from '@shared/api/apiClient';
import {
    ConnectionTypeModel,
    ConnectionValidationRequest,
    ConnectionValidationResult,
    DataConnectionType,
    DataSourceFileModel,
    DataSourceFileUploadConflictBehavior,
    DataSourceFileUploadResult,
    DataSourceModel,
    DataSourceSaveModel,
    DiscoverColumnsRequest,
    DiscoverSourcesRequest,
    SourceColumn,
    SourceObject,
    AssetIdentityMappingCountRequest,
    AssetIdentityMappingCountResult,
} from '../types';

const basePath = 'FacilityApi/api/DataSources';

const toJsonBody = (value: unknown) => value as JsonValue;
const getClientTimeQuery = () => ({ clientTime: DateTimeOffsetHelper.getCurrentDatetimeWithOffset() });

const dataSourcesApi = {
    getConnectionTypes() {
        return apiGet<ConnectionTypeModel[]>(`${basePath}/ConnectionTypes`);
    },
    testConnection(model: ConnectionValidationRequest) {
        return apiPostJson<ConnectionValidationResult>(`${basePath}/Connections/Test`, toJsonBody(model), { query: getClientTimeQuery() });
    },
    discoverSources(model: DiscoverSourcesRequest) {
        return apiPostJson<SourceObject[]>(`${basePath}/Connections/DiscoverSources`, toJsonBody(model));
    },
    discoverColumns(model: DiscoverColumnsRequest) {
        const request = {
            FacilityID: model.FacilityID,
            Type: model.Type,
            ConnectionConfig: model.ConnectionConfig,
            DataConnectionID: model.DataConnectionID,
            Source: model.Source,
        };
        return apiPostJson<SourceColumn[]>(`${basePath}/Connections/DiscoverColumns`, toJsonBody(request));
    },
    getFiles(type: DataConnectionType) {
        return apiGet<DataSourceFileModel[]>(`${basePath}/Files`, { type });
    },
    uploadFile(type: DataConnectionType, file: File, conflictBehavior: DataSourceFileUploadConflictBehavior = 'Reject') {
        const formData = new FormData();
        formData.append('type', String(type));
        formData.append('file', file);
        formData.append('conflictBehavior', conflictBehavior);
        return apiPostForm<DataSourceFileUploadResult>(`${basePath}/Files/Upload`, formData);
    },
    deleteFile(type: DataConnectionType, fileName: string) {
        return apiPostJson<void>(`${basePath}/Files/Delete`, toJsonBody({ Type: type, FileName: fileName }), { responseType: 'none' });
    },
    getSources(facilityID: string) {
        return apiGet<DataSourceModel[]>(`${basePath}/Sources`, { facilityID });
    },
    getSource(id: string, facilityID: string) {
        return apiGet<DataSourceModel>(`${basePath}/Sources/${id}`, { facilityID });
    },
    createSource(model: DataSourceSaveModel) {
        return apiPostJson<DataSourceModel>(`${basePath}/Sources/Create`, toJsonBody(model), { query: getClientTimeQuery() });
    },
    updateSource(model: DataSourceSaveModel) {
        return apiPostJson<DataSourceModel>(`${basePath}/Sources/Update`, toJsonBody(model), { query: getClientTimeQuery() });
    },
    countMappedAssetIdentities(model: AssetIdentityMappingCountRequest) {
        return apiPostJson<AssetIdentityMappingCountResult>(
            `${basePath}/Sources/CountMappedAssetIdentities`,
            toJsonBody(model)
        );
    },
    deleteSource(model: { FacilityID: string; ID: string; VersionToken: string }) {
        return apiPostJson<void>(`${basePath}/Sources/Delete`, toJsonBody(model), {
            query: getClientTimeQuery(),
            responseType: 'none',
        });
    },
};

export default dataSourcesApi;
